import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, ArrowRight, LifeBuoy, Plus } from "lucide-react";
import { SUPPORT } from "@/constants/testIds";
import { useLang } from "@/lib/useLang";
import { PortalHeader, PortalFooter } from "@/components/PortalChrome";
import { supportApi } from "@/lib/api";

const STATUS_TONE = {
  submitted: "bg-[#F0E9DC] text-[#0B1930]",
  reviewing: "bg-[#F0E9DC] text-[#0B1930]",
  scheduled: "bg-[#DDF3E4] text-[#0F5132]",
  resolved: "bg-[#DDF3E4] text-[#0F5132]",
  closed: "bg-[#EDE5D4] text-[#4B5563]",
};

export default function SupportRequestsList() {
  const navigate = useNavigate();
  const { lang, t, toggle } = useLang();
  const [state, setState] = useState({ loading: true, items: [], error: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supportApi.list();
        if (cancelled) return;
        setState({ loading: false, items: data.requests || [], error: "" });
      } catch (err) {
        if (err?.status === 401) {
          navigate("/", { replace: true });
          return;
        }
        if (!cancelled)
          setState({ loading: false, items: [], error: err?.detail || "" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div
      className="min-h-screen flex flex-col zmex-page-bg"
      data-testid={SUPPORT.listPage}
    >
      <PortalHeader t={t} lang={lang} onToggleLang={toggle} />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14 pb-16">
          <button
            type="button"
            onClick={() => navigate("/portal/dashboard")}
            className="inline-flex items-center gap-1.5 text-sm text-[#4B5563] hover:text-[#0B1930] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t.support.backToDashboard}
          </button>
          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F0E9DC]">
                <LifeBuoy className="h-5 w-5 text-[#0B1930]" />
              </span>
              <h1 className="font-heading uppercase tracking-tight text-[#0B1930] text-2xl sm:text-3xl">
                {t.support.listHeading}
              </h1>
            </div>
            <button
              type="button"
              onClick={() => navigate("/portal/support")}
              className="inline-flex items-center gap-2 rounded-full bg-[#0B1930] hover:bg-[#162B4D] text-white px-4 py-2 text-sm font-semibold"
            >
              <Plus className="h-4 w-4" />
              {t.support.listNew}
            </button>
          </div>

          {state.loading ? (
            <div className="mt-10 flex items-center gap-3 text-[#4B5563]">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : state.items.length === 0 ? (
            <div className="mt-8 rounded-2xl bg-white border border-dashed border-[#EDE5D4] p-10 text-center">
              <p className="text-[#4B5563]">{t.support.listEmpty}</p>
              <button
                type="button"
                onClick={() => navigate("/portal/support")}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#C8A46B] hover:bg-[#B8925A] text-[#0B1930] px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors"
              >
                {t.support.listNew}
              </button>
            </div>
          ) : (
            <ul className="mt-6 space-y-3">
              {state.items.map((r) => (
                <li
                  key={r.request_id}
                  className="rounded-2xl bg-white border border-[#EDE5D4] p-4 sm:p-5"
                  data-testid={SUPPORT.listItem(r.request_id)}
                >
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                            STATUS_TONE[r.status] || STATUS_TONE.submitted
                          }`}
                        >
                          {t.support.statusValues[r.status] || r.status}
                        </span>
                        <span className="text-xs text-[#4B5563] font-mono">
                          {r.confirmation_number}
                        </span>
                      </div>
                      <p className="mt-1.5 text-sm font-semibold text-[#0B1930] truncate">
                        {r.subject}
                      </p>
                      <p className="mt-0.5 text-xs text-[#4B5563]">
                        {t.support.categoryLabels[r.category] || r.category}
                        {" · "}
                        {r.account_display_name}
                        {r.location_label ? ` · ${r.location_label}` : ""}
                      </p>
                      <p className="mt-1 text-xs text-[#9AA1AC]">
                        {new Date(r.updated_at || r.created_at).toLocaleString(
                          lang === "es" ? "es-US" : "en-US",
                          { dateStyle: "medium", timeStyle: "short" },
                        )}
                      </p>
                    </div>
                    <Link
                      to={`/portal/support/requests/${r.request_id}`}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-[#B8925A] hover:text-[#0B1930]"
                      data-testid={SUPPORT.listItemView(r.request_id)}
                    >
                      {t.support.viewDetails}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {state.error && (
            <p className="mt-6 text-sm text-red-600">{state.error}</p>
          )}
        </div>
      </main>
      <PortalFooter t={t} />
    </div>
  );
}
